import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { get } from 'data/services/apiService';
import { getApiUrl } from 'data/services/apiConstants';
import Formatters from 'formatters/_Formatter';
import RecordDetailsLink from 'formatters/RecordDetailsLink';

import { BIS, COMBINED } from 'data/dataSourceConstants';

import Highlight from 'components/Highlight';
import SortableHeader from 'components/dataTable/SortableHeader';
import FileUploadButton from 'components/dataTable/FileUploadButton';
import HistoryAccordion from 'components/dataTable/HistoryAccordion';

import './DataTable.css';

// Datasets rendered as job-number groups (one parent filing shown expanded to
// its sibling filings on demand), and what each needs to fetch/hide its children.
const GROUPED_JOB_DATASETS = {
	limitedAlterationApplications: {
		childrenUrl: (jobNum) => `/api/limited-alteration-applications/job/${jobNum}/children`,
		blankChildColumnLabels: ['Drawings', 'Job #'],
	},
	electricalPermitApplications: {
		childrenUrl: (jobNum) => `/api/electrical-permit-applications/job/${jobNum}/children`,
		blankChildColumnLabels: ['Job Number'],
	},
};

function DataTable(props) {
	let {
		columns,
		data,
		dataSet,
		dataSource,
		propertyData,
		isLoading,
		searchString,
		sortField,
		sortDirection,
		onSort,
	} = props;

	const dispatch = useDispatch();
	const authToken = useSelector(state => state.user.authToken);

	const [expandedRows, setExpandedRows] = useState({});
	const [childRecords, setChildRecords] = useState({});
	const [childLoadingStates, setChildLoadingStates] = useState({});

	const toggleRow = (jobNum) => {
		const groupConfig = GROUPED_JOB_DATASETS[dataSet];
		const isExpanding = !expandedRows[jobNum];
		setExpandedRows(prev => ({
			...prev,
			[jobNum]: isExpanding
		}));

		if (isExpanding && !childRecords[jobNum] && groupConfig) {
			setChildLoadingStates(prev => ({
				...prev,
				[jobNum]: true
			}));

			const url = `${getApiUrl()}${groupConfig.childrenUrl(jobNum)}`;
			get({
				url,
				authToken,
				dispatch,
				onSuccess: (resData) => {
					setChildRecords(prev => ({
						...prev,
						[jobNum]: resData
					}));
					setChildLoadingStates(prev => ({
						...prev,
						[jobNum]: false
					}));
				},
				onError: (err) => {
					console.error(`Error fetching child records for ${dataSet}:`, err);
					setChildLoadingStates(prev => ({
						...prev,
						[jobNum]: false
					}));
				}
			});
		}
	};

	let noDataRow = <tr><td colSpan={columns.length + 1} className="nothing-found-row">No Data Available</td></tr>;

	if (isLoading) {
		noDataRow = <tr><td colSpan={columns.length + 1} className="nothing-found-row">Loading...</td></tr>;
	}

	return (
		<table className="table table-striped data-table">
			<thead>
				<tr>
					<th></th>
					{columns.map((col, i) => (
						<SortableHeader
							key={`${col.key}-${col.label}-${i}`}
							column={col}
							sortField={sortField}
							sortDirection={sortDirection}
							onSort={onSort}
							className={col.formatter === 'ViolationImageLink' ? 'text-center' : ''}
						/>
					))}
				</tr>
			</thead>
			<tbody>
				{(data.length === 0 || isLoading) && noDataRow}
				{!isLoading && data.map((row, i) => {
					const groupConfig = GROUPED_JOB_DATASETS[dataSet];
					if (groupConfig) {
						const isExpanded = !!expandedRows[row.job_number];
						const hasChildren = row.has_children;
						const boldRow = dataSet === 'electricalPermitApplications' && row.isOpen === 1;

						return (
							<React.Fragment key={row.id || `row-${i}`}>
								{/* Parent Row */}
								<tr style={boldRow ? { fontWeight: 'bold' } : undefined}>
									<td className="data-table-buttons-cell">
										<div className="data-table-buttons-wrapper">
											<RecordDetailsLink isBis={dataSource === BIS} dataSource={dataSource} row={row} dataSet={dataSet} />
											{dataSource !== COMBINED && (<FileUploadButton dataSet={dataSet} dataSource={dataSource} row={row} />)}
										</div>
									</td>
									{columns.map((col, j) => {
										let rawContent = row[col.key] || row[col.altKey];
										let cellContent = rawContent || '-';

										if (col.key === 'filing_number') {
											return (
												<td key={`${row.id}-${i}-${col.key}-${col.label}-${j}`}>
													<div style={{ display: 'flex', alignItems: 'center' }}>
														<span>{cellContent}</span>
														{hasChildren && (
															<span
																onClick={() => toggleRow(row.job_number)}
																style={{ cursor: 'pointer', marginLeft: '6px' }}
															>
																<i className={`bi bi-chevron-${isExpanded ? 'up' : 'down'}`} />
															</span>
														)}
													</div>
												</td>
											);
										}

										if (col.formatter) {
											let Formatter = Formatters[col.formatter];
											if (Formatter && rawContent) {
												cellContent = <Formatter text={rawContent} format={col.format} rowData={row} isChildRow={false} propertyData={propertyData} dataSet={dataSet} dataSource={dataSource} searchString={searchString} />;
											}
										}

										return (
											<td key={`${row.id}-${i}-${col.key}-${col.label}-${j}`}>
												{typeof cellContent === 'string' ? <Highlight text={cellContent} substring={searchString} /> : cellContent}
											</td>
										);
									})}
								</tr>

								{/* Child Rows */}
								{isExpanded && hasChildren && (
									childLoadingStates[row.job_number] ? (
										<tr>
											<td className="data-table-buttons-cell"></td>
											<td colSpan={columns.length} className="nothing-found-row text-center py-2">
												<div className="spinner-border spinner-border-sm text-secondary me-2" role="status" style={{ width: '1rem', height: '1rem' }}>
													<span className="visually-hidden">Loading...</span>
												</div>
												Loading child filings...
											</td>
										</tr>
									) : (
										(childRecords[row.job_number] || []).length > 0 ? (
											(childRecords[row.job_number] || []).map((childRow, childIndex) => {
												const boldChildRow = dataSet === 'electricalPermitApplications' && childRow.isOpen === 1;
												return (
												<tr key={`${row.id || i}-child-${childIndex}`} className="laa-child-row" style={boldChildRow ? { fontWeight: 'bold' } : undefined}>
													<td className="data-table-buttons-cell">
														{/* Empty cell */}
													</td>
													{columns.map((col, j) => {
														if (groupConfig.blankChildColumnLabels.includes(col.label)) {
															return <td key={`${row.id}-${i}-child-${childIndex}-${col.label}-${j}`}></td>;
														}

														let rawContent = childRow[col.key] || childRow[col.altKey];
														let cellContent = rawContent || '-';

														if (col.formatter && col.formatter !== 'StatusBadge') {
															let Formatter = Formatters[col.formatter];
															if (Formatter && rawContent) {
																cellContent = <Formatter text={rawContent} format={col.format} rowData={childRow} isChildRow={true} propertyData={propertyData} dataSet={dataSet} dataSource={dataSource} searchString={searchString} />;
															}
														}

														return (
															<td key={`${row.id}-${i}-child-${childIndex}-${col.key}-${col.label}-${j}`}>
																{typeof cellContent === 'string' ? <Highlight text={cellContent} substring={searchString} /> : cellContent}
															</td>
														);
													})}
												</tr>
											);
										})
										) : (
											<tr>
												<td className="data-table-buttons-cell"></td>
												<td colSpan={columns.length} className="nothing-found-row text-center py-2">
													No child filings found.
												</td>
											</tr>
										)
									)
								)}
							</React.Fragment>
						);
					}

					// Default/Fallback: standard row with HistoryAccordion
					return (
						<React.Fragment key={row.id || `row-${i}`}>
							<tr>
								<td className="data-table-buttons-cell">
									<div className="data-table-buttons-wrapper">
										<RecordDetailsLink isBis={dataSource === BIS} dataSource={dataSource} row={row} dataSet={dataSet} />
										{dataSource !== COMBINED && (<FileUploadButton dataSet={dataSet} dataSource={dataSource} row={row} />)}
									</div>
								</td>
								{columns.map((col, j) => {
									let rawContent = row[col.key] || row[col.altKey];
									let cellContent = rawContent || '-';
									if (col.formatter) {
										let Formatter = Formatters[col.formatter];
										if (Formatter && rawContent) {
											cellContent = <Formatter text={rawContent} format={col.format} rowData={row} propertyData={propertyData} dataSet={dataSet} dataSource={dataSource} searchString={searchString} />;
										}
									}
									return (
										<td
											key={`${row.id}-${i}-${col.key}-${col.label}-${j}`}
											className={col.formatter === 'ViolationImageLink' ? 'text-center' : ''}
										>
											{typeof cellContent === 'string' ? <Highlight text={cellContent} substring={searchString} /> : cellContent}
										</td>
									);
								})}
							</tr>
							{row.history && row.history.length > 0 && (
								<HistoryAccordion
									history={row.history}
									columns={columns}
									dataSet={dataSet}
									dataSource={dataSource}
									propertyData={propertyData}
									searchString={searchString}
								/>
							)}
						</React.Fragment>
					);
				})}
			</tbody>
		</table>
	);
}

export default DataTable;
